// Plain-language labels of the Assessments area, shared by the list, the documents and the assessment page: the stage of an assessment as its status chip (queued, running with the topics done, ready for review, in review with the topics reviewed, approved, failed, cancelled) and the stage filter, when a run's job has ended or is gone (an interrupted run waits to resume unless it was stopped) and the chip of a run that stopped without the assessment saying so, the reading state of a capability document with its live progress line, the kinds and levels of capability entries, plant types, the "family · version" grid-code label and the default assessment name; the vocabulary the API does not send with each value: the three levels (short and long labels, the PoC note), tone names and icons for filters and the legend, review states, the verdict choices of the change form (every status, developer scope included) and when a level cell's headline only repeats its label; no enum value reaches the screen raw.
import {
  BanIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  CircleMinusIcon,
  CircleXIcon,
  ClipboardListIcon,
  ClockIcon,
  InfoIcon,
  ListChecksIcon,
  LoaderCircleIcon,
  type LucideIcon,
} from "lucide-react";

import type {
  AssessmentGridCode,
  AssessmentSummary,
  CapabilityDocumentStatus,
  CapabilityKind,
  CapabilityLevel,
  GridConnection,
  JobProgress,
  LevelCell,
  LevelVerdictStatus,
  OperationJob,
  RefersTo,
  ReviewCounts,
  ReviewState,
  VerdictTone,
} from "@/api/__generated__";
import { isApiProblem } from "@/api/problem";
import type { BadgeProps } from "@/components/ui/badge";
import { formatNumber } from "@/lib/format";

export type ChipVariant = NonNullable<BadgeProps["variant"]>;

export interface ChipStyle {
  label: string;
  variant: ChipVariant;
  icon: LucideIcon;
  spinning?: boolean;
}

export const ASSESSMENT_STAGES = [
  "queued",
  "running",
  "ready",
  "in_review",
  "approved",
  "failed",
  "cancelled",
] as const;
export type AssessmentStage = (typeof ASSESSMENT_STAGES)[number];

export const STAGE_CHIPS: Record<AssessmentStage, ChipStyle> = {
  queued: { label: "Queued", variant: "brand", icon: ClockIcon },
  running: { label: "Running", variant: "brand", icon: LoaderCircleIcon, spinning: true },
  ready: { label: "Ready for review", variant: "brand", icon: ClipboardListIcon },
  in_review: { label: "In review", variant: "neutral", icon: ListChecksIcon },
  approved: { label: "Approved", variant: "ok", icon: CircleCheckIcon },
  failed: { label: "Failed", variant: "attn", icon: CircleXIcon },
  cancelled: { label: "Cancelled", variant: "grey", icon: BanIcon },
};

export const STAGE_FILTER_LABELS: Record<AssessmentStage, string> = {
  queued: "Queued",
  running: "Running",
  ready: "Ready for review",
  in_review: "In review",
  approved: "Approved",
  failed: "Failed",
  cancelled: "Cancelled",
};

export function assessmentStage(
  assessment: Pick<AssessmentSummary, "status" | "in_review">,
): AssessmentStage {
  if (assessment.status === "ready" && assessment.in_review) {
    return "in_review";
  }
  return assessment.status;
}

export function reviewedCount(review: ReviewCounts): number {
  return review.approved + review.changed;
}

export function reviewLine(review: ReviewCounts): string {
  return `${formatNumber(reviewedCount(review))} of ${formatNumber(review.total)} reviewed`;
}

export function awaitsReview(assessment: Pick<AssessmentSummary, "status" | "review">): boolean {
  return assessment.status === "ready" && assessment.review.pending > 0;
}

export function countToReview(assessments: readonly Pick<AssessmentSummary, "status" | "review">[]): number {
  return assessments.filter(awaitsReview).length;
}

export function isRunning(assessment: Pick<AssessmentSummary, "status">): boolean {
  return assessment.status === "queued" || assessment.status === "running";
}

export const RUN_STOPPED_CHIP: ChipStyle = { label: "Run stopped", variant: "attn", icon: CircleXIcon };

export function runJobEnded(
  job: Pick<OperationJob, "status" | "auto_resumed_by" | "cancel_requested_at"> | undefined,
): boolean {
  if (job === undefined) {
    return false;
  }
  if (job.status === "interrupted") {
    return job.auto_resumed_by === null && job.cancel_requested_at !== null;
  }
  return job.status !== "queued" && job.status !== "running";
}

export function jobGone(error: unknown): boolean {
  return isApiProblem(error) && error.status === 404;
}

export function runningLabel(
  job: Pick<OperationJob, "status" | "progress" | "queue"> | undefined,
  fallback: { done: number; total: number | null },
): string {
  const progress = job?.progress ?? null;
  if (job?.status === "queued") {
    return "Queued";
  }
  if (job?.status === "interrupted") {
    return "Waiting to resume…";
  }
  if (progress?.phase === "cancelling") {
    return "Stopping…";
  }
  if (progress?.phase === "starting") {
    return "Starting…";
  }
  const done = progress?.items_done ?? null;
  const total = progress?.items_total ?? null;
  if (done !== null && total !== null) {
    return `Running ${formatNumber(done)}/${formatNumber(total)}`;
  }
  if (fallback.total !== null && fallback.total > 0) {
    return `Running ${formatNumber(fallback.done)}/${formatNumber(fallback.total)}`;
  }
  return "Running";
}

export function itemsFraction(progress: JobProgress | null): number | null {
  const done = progress?.items_done ?? null;
  const total = progress?.items_total ?? null;
  if (done === null || total === null || total <= 0) {
    return null;
  }
  return Math.min(1, done / total);
}

export const READING_CHIPS: Record<CapabilityDocumentStatus, ChipStyle> = {
  uploaded: { label: "Not read yet", variant: "outline", icon: CircleDashedIcon },
  extracting: { label: "Reading…", variant: "brand", icon: LoaderCircleIcon, spinning: true },
  ready: { label: "Ready", variant: "ok", icon: CircleCheckIcon },
  failed: { label: "Failed", variant: "attn", icon: CircleXIcon },
};

export function readingProgressLine(
  job: Pick<OperationJob, "status" | "queue"> & { progress: JobProgress | null },
): string | null {
  if (job.status === "queued") {
    return job.queue === null ? "Starting…" : "Waiting to start";
  }
  if (job.status !== "running") {
    return null;
  }
  const progress = job.progress;
  if (progress === null || progress.phase === "starting" || progress.phase === "queued") {
    return "Starting…";
  }
  if (progress.phase === "cancelling") {
    return "Stopping…";
  }
  if (progress.items_done !== null && progress.items_total !== null && progress.items_total > 0) {
    const page = Math.min(progress.items_total, progress.items_done + 1);
    return `Page ${formatNumber(page)} of ${formatNumber(progress.items_total)}`;
  }
  return null;
}

export const CAPABILITY_KINDS: readonly CapabilityKind[] = [
  "capability",
  "option",
  "not_available",
  "external_reference",
];

export const CAPABILITY_KIND_LABELS: Record<CapabilityKind, string> = {
  capability: "Standard",
  option: "Option",
  not_available: "Not available",
  external_reference: "In a referenced document",
};

export const CAPABILITY_LEVELS: readonly CapabilityLevel[] = ["wtg", "ppc", "scada", "wind_farm"];

export const CAPABILITY_LEVEL_LABELS: Record<CapabilityLevel, string> = {
  wtg: "WTG",
  ppc: "PPC",
  scada: "SCADA",
  wind_farm: "Wind farm",
};

export const CAPABILITY_LEVEL_NAMES: Record<CapabilityLevel, string> = {
  wtg: "Turbine",
  ppc: "Plant controller",
  scada: "SCADA",
  wind_farm: "Wind farm",
};

export const PLANT_TYPES = ["A", "B", "C", "D"] as const;
export type PlantType = (typeof PLANT_TYPES)[number];

export function plantTypeLabel(plantType: PlantType | null): string {
  return plantType === null ? "Not specified" : `Type ${plantType}`;
}

export const GRID_CONNECTION_LABELS: Record<GridConnection, string> = {
  new: "New (EU Code User)",
  existing: "Existing (GB Code User)",
};

export function gridConnectionLabel(connection: GridConnection | null): string {
  return connection === null ? "Not specified" : GRID_CONNECTION_LABELS[connection];
}

export function gridCodeLabel(gridCode: Pick<AssessmentGridCode, "family_title" | "version_label">): string {
  return `${gridCode.family_title} · ${gridCode.version_label}`;
}

export function documentTitle(document: { title: string | null; file_name: string }): string {
  return document.title ?? document.file_name;
}

export function defaultAssessmentName(productTitle: string, versionLabel: string): string {
  return `${productTitle} · ${versionLabel}`;
}

export function appliesToText(appliesTo: readonly string[]): string {
  return appliesTo.length === 0 ? "All" : appliesTo.join(", ");
}

export type LevelKey = "wtg" | "ppc" | "poc";

export const LEVELS: readonly LevelKey[] = ["wtg", "ppc", "poc"];

export const LEVEL_LABELS: Record<LevelKey, { short: string; long: string }> = {
  wtg: { short: "WTG", long: "Turbine · LV terminals" },
  ppc: { short: "PPC", long: "Plant controller · point of connection" },
  poc: { short: "PoC", long: "Wind farm · point of connection" },
};

export const POC_NOTE = "At PoC, compliance also depends on the developer's collector system.";

export const TONES: readonly VerdictTone[] = [
  "positive",
  "positive_soft",
  "info",
  "caution",
  "negative",
  "muted",
];

export const TONE_LABELS: Record<VerdictTone, string> = {
  positive: "Compliant",
  positive_soft: "Compliance expected",
  info: "Other level, study or developer",
  caution: "Open or to be agreed",
  negative: "Not compliant or not available",
  muted: "Not applicable or no requirement",
};

export const TONE_ICONS: Record<VerdictTone, LucideIcon> = {
  positive: CircleCheckIcon,
  positive_soft: CircleDashedIcon,
  info: InfoIcon,
  caution: CircleAlertIcon,
  negative: CircleXIcon,
  muted: CircleMinusIcon,
};

export const REVIEW_LABELS: Record<ReviewState, string> = {
  pending: "Pending",
  approved: "Approved",
  changed: "Changed",
};

export const REVIEW_STATES: readonly ReviewState[] = ["pending", "approved", "changed"];

export interface StatusChoice {
  value: LevelVerdictStatus;
  label: string;
  tone: VerdictTone;
}

const STATUS_CHOICE_TEXT: Record<LevelVerdictStatus, Omit<StatusChoice, "value">> = {
  compliant: { label: "Compliant", tone: "positive" },
  compliance_expected: { label: "Compliance expected", tone: "positive_soft" },
  assessed_at_other_level: { label: "Assessed at another level", tone: "info" },
  wind_farm_study: { label: "Wind farm study needed", tone: "info" },
  developer_scope: { label: "Developer scope", tone: "info" },
  to_be_agreed: { label: "To be agreed", tone: "caution" },
  open: { label: "Open (information missing)", tone: "caution" },
  not_compliant: { label: "Not compliant", tone: "negative" },
  not_available: { label: "Not available", tone: "negative" },
  not_applicable: { label: "Not applicable", tone: "muted" },
  no_requirement: { label: "No requirement", tone: "muted" },
};

export const STATUS_CHOICES: readonly StatusChoice[] = (
  Object.keys(STATUS_CHOICE_TEXT) as LevelVerdictStatus[]
).map((value) => ({ value, ...STATUS_CHOICE_TEXT[value] }));

export function otherLevels(level: LevelKey): Exclude<RefersTo, "none">[] {
  return LEVELS.filter((other) => other !== level);
}

type HeadlineCell = Pick<LevelCell, "status" | "label" | "headline">;

const SEPARATORS = new Set([";", ":", ",", "–", "—", "-"]);

function withoutLabel(headline: string, label: string): string | null {
  for (const prefix of [label, label.replace(/\s*\(.*\)$/u, "")]) {
    if (prefix !== "" && headline.toLowerCase().startsWith(prefix.toLowerCase())) {
      const rest = headline.slice(prefix.length).trimStart();
      if (rest === "" || rest.replace(/[.\s]+$/u, "") === "") {
        return "";
      }
      if (SEPARATORS.has(rest.charAt(0))) {
        const text = rest.slice(1).trim();
        return text.charAt(0).toUpperCase() + text.slice(1);
      }
    }
  }
  return null;
}

export function headlineText(cell: HeadlineCell): string {
  const headline = cell.headline.trim();
  return withoutLabel(headline, cell.label) ?? headline;
}

export function showsHeadline(cell: HeadlineCell): boolean {
  return cell.status !== "no_requirement" && headlineText(cell) !== "";
}
